import { useState, useCallback, useRef, useEffect } from 'react'
import { transcribeAudio, synthesizeText } from '@/shared/lib/api'

interface UseVoiceChatOptions {
  messages: { sender: string; text: string }[]
  isStreaming: boolean
  sendMessage: (text: string) => void
  silenceThreshold?: number
  silenceDurationMs?: number
}

interface UseVoiceChatReturn {
  isVoiceChatActive: boolean
  isListening: boolean
  isSpeaking: boolean
  isTranscribing: boolean
  toggleVoiceChat: () => void
  stopVoiceChat: () => void
}

function pickMimeType(): string {
  const candidates = [
    'audio/webm;codecs=opus',
    'audio/webm',
    'audio/mp4',
    'audio/ogg;codecs=opus',
  ]
  for (const mime of candidates) {
    if (MediaRecorder.isTypeSupported(mime)) return mime
  }
  return ''
}

// Each recording cycle gets a unique ID so onstop handlers
// can tell if they belong to the current cycle or a stale one.
let cycleCounter = 0

export function useVoiceChat({
  messages,
  isStreaming,
  sendMessage,
  silenceThreshold = 0.01,
  silenceDurationMs = 1500,
}: UseVoiceChatOptions): UseVoiceChatReturn {
  const [isVoiceChatActive, setIsVoiceChatActive] = useState(false)
  const [isListening, setIsListening] = useState(false)
  const [isSpeaking, setIsSpeaking] = useState(false)
  const [isTranscribing, setIsTranscribing] = useState(false)

  const activeRef = useRef(false)
  const prevLoadingRef = useRef(false)
  const prevMessageCountRef = useRef(messages.length)
  const audioRef = useRef<AudioBufferSourceNode | null>(null)
  const audioCtxRef = useRef<AudioContext | null>(null)
  const currentCycleRef = useRef(0)

  // Refs to break circular deps
  const startListeningRef = useRef<() => Promise<void>>()
  const sendMessageRef = useRef(sendMessage)
  sendMessageRef.current = sendMessage

  const restartListening = useCallback(() => {
    void startListeningRef.current?.()
  }, [])

  const speakText = useCallback(async (text: string) => {
    if (!text.trim()) return

    // Stop any currently playing audio
    if (audioRef.current) {
      try { audioRef.current.stop() } catch { /* already stopped */ }
      audioRef.current = null
    }

    setIsSpeaking(true)

    try {
      const wavBuffer = await synthesizeText(text)

      if (!activeRef.current) {
        setIsSpeaking(false)
        return
      }

      const ctx = audioCtxRef.current ?? new AudioContext()
      audioCtxRef.current = ctx
      if (ctx.state === 'suspended') await ctx.resume()

      const audioBuffer = await ctx.decodeAudioData(wavBuffer)
      const source = ctx.createBufferSource()
      source.buffer = audioBuffer
      source.connect(ctx.destination)
      audioRef.current = source

      source.onended = () => {
        audioRef.current = null
        setIsSpeaking(false)
        if (activeRef.current) restartListening()
      }

      source.start()
    } catch (err) {
      console.error('[voice-chat] TTS failed, falling back to Web Speech API:', err)
      // Fallback to browser TTS if backend is unavailable
      if (window.speechSynthesis) {
        const utterance = new SpeechSynthesisUtterance(text)
        utterance.rate = 1.05
        utterance.onend = () => {
          setIsSpeaking(false)
          if (activeRef.current) restartListening()
        }
        utterance.onerror = () => {
          setIsSpeaking(false)
          if (activeRef.current) restartListening()
        }
        window.speechSynthesis.speak(utterance)
      } else {
        setIsSpeaking(false)
        if (activeRef.current) restartListening()
      }
    }
  }, [restartListening])

  const handleRecordingComplete = useCallback(async (blob: Blob) => {
    if (blob.size === 0 || !activeRef.current) return

    setIsTranscribing(true)
    try {
      const { text } = await transcribeAudio(blob)
      if (text && activeRef.current) {
        sendMessageRef.current(text)
        // Don't restart listening here — the effect watching isStreaming will
        // trigger speakText when the AI responds, and speakText.onend restarts listening.
      } else if (activeRef.current) {
        restartListening()
      }
    } catch (err) {
      console.error('[voice-chat] Transcription failed:', err)
      if (activeRef.current) restartListening()
    } finally {
      setIsTranscribing(false)
    }
  }, [restartListening])

  const startListening = useCallback(async () => {
    if (!activeRef.current) return

    // Claim a new cycle — any onstop from a previous cycle will see it's stale
    const myCycle = ++cycleCounter
    currentCycleRef.current = myCycle

    console.log('[voice-chat] startListening cycle', myCycle)

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
      if (!activeRef.current || currentCycleRef.current !== myCycle) {
        stream.getTracks().forEach((t) => t.stop())
        return
      }

      const audioContext = new AudioContext()
      // Chrome may suspend AudioContext when not triggered by user gesture
      if (audioContext.state === 'suspended') {
        await audioContext.resume()
      }

      if (!activeRef.current || currentCycleRef.current !== myCycle) {
        audioContext.close().catch(() => {})
        stream.getTracks().forEach((t) => t.stop())
        return
      }

      const source = audioContext.createMediaStreamSource(stream)
      const analyser = audioContext.createAnalyser()
      analyser.fftSize = 512
      source.connect(analyser)

      const mimeType = pickMimeType()
      const recorder = new MediaRecorder(stream, mimeType ? { mimeType } : undefined)
      const chunks: BlobPart[] = []
      let hasSpoken = false
      let silenceTimer: ReturnType<typeof setTimeout> | null = null
      let vadRaf: number | null = null

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunks.push(e.data)
      }

      recorder.onstop = () => {
        console.log('[voice-chat] recorder.onstop cycle', myCycle, 'current', currentCycleRef.current, 'hasSpoken', hasSpoken)

        // Clean up THIS cycle's resources (use local vars, not refs)
        stream.getTracks().forEach((t) => t.stop())
        audioContext.close().catch(() => {})
        if (vadRaf !== null) cancelAnimationFrame(vadRaf)
        if (silenceTimer !== null) clearTimeout(silenceTimer)

        // If this is a stale cycle, discard
        if (currentCycleRef.current !== myCycle) return

        setIsListening(false)

        const actualMime = recorder.mimeType || mimeType || 'audio/webm'
        const blob = new Blob(chunks, { type: actualMime })

        if (hasSpoken && blob.size > 0) {
          void handleRecordingComplete(blob)
        } else if (activeRef.current) {
          restartListening()
        }
      }

      recorder.start(250)
      setIsListening(true)

      // Voice activity detection loop — all state is local to this cycle
      const dataArray = new Float32Array(analyser.fftSize)
      let isSilent = true

      const checkAudio = () => {
        // Stop if this cycle is no longer current
        if (!activeRef.current || currentCycleRef.current !== myCycle) return

        analyser.getFloatTimeDomainData(dataArray)
        let sum = 0
        for (let i = 0; i < dataArray.length; i++) {
          sum += dataArray[i] * dataArray[i]
        }
        const rms = Math.sqrt(sum / dataArray.length)

        if (rms > silenceThreshold) {
          hasSpoken = true
          isSilent = false
          if (silenceTimer !== null) {
            clearTimeout(silenceTimer)
            silenceTimer = null
          }
        } else if (!isSilent) {
          isSilent = true
          if (silenceTimer === null && hasSpoken) {
            silenceTimer = setTimeout(() => {
              silenceTimer = null
              console.log('[voice-chat] silence detected, stopping recorder cycle', myCycle)
              if (recorder.state !== 'inactive') {
                recorder.stop()
              }
            }, silenceDurationMs)
          }
        }

        vadRaf = requestAnimationFrame(checkAudio)
      }

      vadRaf = requestAnimationFrame(checkAudio)
    } catch (err) {
      console.error('[voice-chat] Mic access failed:', err)
      setIsListening(false)
    }
  }, [silenceThreshold, silenceDurationMs, handleRecordingComplete, restartListening])

  // Keep the ref current
  startListeningRef.current = startListening

  // Watch for AI response completion — triggers when isStreaming goes true → false
  useEffect(() => {
    const wasStreaming = prevLoadingRef.current
    const hadMessages = prevMessageCountRef.current
    prevLoadingRef.current = isStreaming
    prevMessageCountRef.current = messages.length

    if (!activeRef.current) return
    if (!wasStreaming || isStreaming) return
    if (messages.length <= hadMessages) return

    const lastMessage = messages[messages.length - 1]
    if (lastMessage?.sender === 'ai' && lastMessage.text) {
      // Backend voice processor handles markdown stripping, list conversion, and length limiting
      speakText(lastMessage.text)
    }
  }, [isStreaming, messages, speakText])

  const stopVoiceChat = useCallback(() => {
    activeRef.current = false
    currentCycleRef.current = ++cycleCounter // invalidate any running cycle
    setIsVoiceChatActive(false)
    setIsListening(false)
    setIsTranscribing(false)
    setIsSpeaking(false)
    if (audioRef.current) {
      try { audioRef.current.stop() } catch { /* already stopped */ }
      audioRef.current = null
    }
    window.speechSynthesis?.cancel()
  }, [])

  const toggleVoiceChat = useCallback(() => {
    if (activeRef.current) {
      stopVoiceChat()
    } else {
      activeRef.current = true
      setIsVoiceChatActive(true)
      void startListening()
    }
  }, [stopVoiceChat, startListening])

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      activeRef.current = false
      currentCycleRef.current = ++cycleCounter
      if (audioRef.current) {
        try { audioRef.current.stop() } catch { /* already stopped */ }
      }
      audioCtxRef.current?.close().catch(() => {})
      window.speechSynthesis?.cancel()
    }
  }, [])

  return {
    isVoiceChatActive,
    isListening,
    isSpeaking,
    isTranscribing,
    toggleVoiceChat,
    stopVoiceChat,
  }
}
